import axios from 'axios';
import { getStoredToken } from '../../login/api/loginApi';
import { attachAuthInterceptors } from '../../_shared/auth/authInterceptor';
import type {
  DevStandardsDocument,
  DevStandardsSummary,
  PageResponse,
  UploadDevStandardsRequest,
} from '../types/devStandards';

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE ?? '/api/eraf',
  headers: { 'Content-Type': 'application/json' },
});
attachAuthInterceptors(apiClient);

// Bearer 토큰 첨부
apiClient.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// ApiResponse<T> 언래핑
apiClient.interceptors.response.use(
  (response) => {
    const d = response.data as Record<string, unknown> | undefined;
    if (d && typeof d === 'object' && 'success' in d) {
      if (d.success === true) {
        return { ...response, data: d['data'] };
      }
      const err = d['error'] as Record<string, unknown> | undefined;
      return Promise.reject({
        code: err?.['code'] ?? `HTTP-${response.status}`,
        message: err?.['message'] ?? 'Unknown error',
        traceId: (err?.['traceId'] as string) ?? response.headers?.['x-trace-id'],
      });
    }
    return response;
  },
  (error) => {
    const errData = error.response?.data?.error as Record<string, unknown> | undefined;
    const code = errData?.['code'] ?? `HTTP-${error.response?.status ?? 0}`;
    const message =
      (errData?.['message'] as string) ??
      (error.response?.data?.message as string) ??
      error.message ??
      'Network error';
    const traceId = (error.response?.headers?.['x-trace-id'] as string) ?? undefined;
    return Promise.reject({ code, message, traceId });
  }
);

export const devStandardsApi = {
  /**
   * GET /standards/active — 현재 활성 문서 전문
   * 404 (STD-404-001) 시 null 반환 (empty state)
   */
  getActive: async (): Promise<DevStandardsDocument | null> => {
    try {
      const res = await apiClient.get<unknown, { data: DevStandardsDocument }>(
        '/standards/active'
      );
      return res.data;
    } catch (err) {
      const e = err as { code?: string };
      if (e?.code === 'STD-404-001' || e?.code === 'HTTP-404') {
        return null;
      }
      throw err;
    }
  },

  /**
   * GET /standards?page&size&sort — 버전 목록 (요약, 전문 미포함)
   */
  listVersions: (
    page = 0,
    size = 20
  ): Promise<PageResponse<DevStandardsSummary>> =>
    apiClient
      .get<unknown, { data: PageResponse<DevStandardsSummary> }>(
        `/standards?page=${page}&size=${size}&sort=exportedAt,desc`
      )
      .then((res) => res.data),

  /**
   * GET /standards/{id} — 특정 버전 전문
   */
  getVersion: (id: number): Promise<DevStandardsDocument> =>
    apiClient
      .get<unknown, { data: DevStandardsDocument }>(`/standards/${id}`)
      .then((res) => res.data),

  /**
   * POST /standards — 신규 업로드 (ROLE_ADMIN 전용)
   */
  upload: (body: UploadDevStandardsRequest): Promise<DevStandardsDocument> =>
    apiClient
      .post<unknown, { data: DevStandardsDocument }>('/standards', body)
      .then((res) => res.data),
};
